/**
 * AddressForm — reusable form untuk Tambah/Edit Alamat Customer.
 *
 * Dipakai oleh: Profile (Tab Alamat) dan Checkout (Dialog "Tambah Alamat").
 *
 * Prinsip (P0 fix Cascading Address):
 *  - Wilayah alamat DIMILIKI Dynicty. Bukan Jubelio, bukan provider apapun.
 *  - Cascading: Provinsi → Kota/Kabupaten → Kecamatan → Kelurahan.
 *  - Kode Pos diketik manual setelah wilayah lengkap (dataset tidak
 *    memuat kode pos di level kelurahan — kami tidak mengarang data).
 *  - Save berhasil TANPA Jubelio.
 */
import React, { useEffect, useRef, useState } from "react";
import { MapPin, Package, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import IdLocationPicker from "@/components/IdLocationPicker";
import LocationPicker from "@/components/LocationPicker";

const DEFAULT_FORM = {
  label: "Rumah",
  recipient_name: "",
  phone: "",
  address: "",
  // Cascade Dynicty (BPS ids + human names)
  id_province_id: "",
  id_regency_id: "",
  id_district_id: "",
  id_village_id: "",
  id_village_name: "",
  province: "",
  city: "",
  district: "",
  subdistrict: "",
  postal_code: "",
  is_default: false,
  coordinate: "",
};

export default function AddressForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial || DEFAULT_FORM);
  const [pinOpen, setPinOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const enrichedRef = useRef(false);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Legacy backfill: kalau initial address hanya punya text province/city/
  // district (dari Jubelio picker lama), coba cocokkan otomatis ke BPS id
  // agar cascading pre-select. Best-effort case-insensitive name match. Kalau
  // gagal, user pilih ulang saja — tidak crash.
  useEffect(() => {
    if (enrichedRef.current) return;
    if (!initial) return;
    if (initial.id_province_id) return; // sudah punya BPS id
    if (!initial.province && !initial.city) return; // nothing to enrich
    enrichedRef.current = true;
    const norm = (s) => (s || "").trim().toUpperCase().replace(/^KOTA\s+|^KABUPATEN\s+/, "");
    (async () => {
      try {
        const { data: provs } = await api.get("/locations/provinces");
        const p = provs.find((x) => norm(x.name) === norm(initial.province));
        if (!p) return;
        const patch = { id_province_id: p.id, province: p.name };
        const { data: regs } = await api.get(`/locations/regencies?province_id=${p.id}`);
        const r = regs.find((x) => norm(x.name) === norm(initial.city));
        if (r) {
          patch.id_regency_id = r.id;
          patch.city = r.name;
          const { data: dists } = await api.get(`/locations/districts?regency_id=${r.id}`);
          const d = dists.find((x) => norm(x.name) === norm(initial.district));
          if (d) {
            patch.id_district_id = d.id;
            patch.district = d.name;
            const { data: villages } = await api.get(`/locations/villages?district_id=${d.id}`);
            const v = villages.find((x) => norm(x.name) === norm(initial.subdistrict));
            if (v) {
              patch.id_village_id = v.id;
              patch.id_village_name = v.name;
              patch.subdistrict = v.name;
            }
          }
        }
        setForm((f) => ({ ...f, ...patch }));
      } catch (_e) {
        /* enrichment optional — silent fail */
      }
    })();
  }, [initial]);

  const submit = async (e) => {
    e.preventDefault();
    // Wajib pilih wilayah lengkap sampai kelurahan.
    if (!form.id_province_id || !form.id_regency_id || !form.id_district_id || !form.id_village_id) {
      toast.error(
        "Lengkapi Provinsi, Kota/Kabupaten, Kecamatan, dan Kelurahan terlebih dahulu",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (initial?.id) await api.put(`/addresses/${initial.id}`, payload);
      else await api.post("/addresses", payload);
      toast.success("Alamat tersimpan");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-3 max-h-[70vh] overflow-y-auto pr-1"
      data-testid="address-form"
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Label</Label>
          <Input
            value={form.label}
            onChange={(e) => upd("label", e.target.value)}
            placeholder="Rumah / Kantor"
            data-testid="addr-label"
          />
        </div>
        <div>
          <Label>Nama Penerima</Label>
          <Input
            value={form.recipient_name}
            onChange={(e) => upd("recipient_name", e.target.value)}
            required
            data-testid="addr-recipient"
          />
        </div>
      </div>

      <div>
        <Label>No. WhatsApp</Label>
        <Input
          value={form.phone}
          onChange={(e) => upd("phone", e.target.value)}
          required
          data-testid="addr-phone"
        />
      </div>

      <div>
        <Label>Alamat Lengkap</Label>
        <Textarea
          value={form.address}
          onChange={(e) => upd("address", e.target.value)}
          placeholder="Contoh: Jl. Melati No. 10 RT 03 / RW 05, samping mini market"
          required
          data-testid="addr-address"
        />
      </div>

      {/* Info card wilayah — informatif, tanpa toggle */}
      <div
        className="p-3 rounded-xl border border-[#E5E5E5] system-soft-bg space-y-1"
        data-testid="shipping-info-card"
      >
        <div className="text-sm font-bold flex items-center gap-1.5">
          <Package size={14} /> Wilayah Pengiriman
        </div>
        <div className="text-[11px] text-[#7A5440] leading-relaxed">
          Pilih Provinsi, Kota/Kabupaten, Kecamatan, dan Kelurahan agar
          alamat tersimpan lengkap.
        </div>
      </div>

      {/* Cascading Dynicty — selalu tersedia, tidak butuh Jubelio */}
      <IdLocationPicker
        value={form}
        onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
      />

      <div>
        <Label>Kode Pos</Label>
        <Input
          value={form.postal_code}
          onChange={(e) => upd("postal_code", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
          placeholder="Contoh: 11730"
          inputMode="numeric"
          maxLength={5}
          data-testid="addr-postal"
        />
        <div className="text-[11px] text-[#7A5440] mt-1">
          Isi kode pos 5 digit sesuai alamat. Kalau lupa, cek pada surat
          resmi atau tanya tetangga.
        </div>
      </div>

      <Label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={!!form.is_default}
          onChange={(e) => upd("is_default", e.target.checked)}
          data-testid="addr-default"
        />{" "}
        Jadikan alamat utama
      </Label>

      {/* PIN Lokasi — tanpa istilah "Latitude/Longitude/Coordinate" */}
      <div
        className="p-3 rounded-xl border border-[#E5E5E5] system-soft-bg space-y-2"
        data-testid="address-pin-section"
      >
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <div className="text-sm font-bold flex items-center gap-1.5">
              <MapPin size={14} /> Pin Lokasi
            </div>
            <div
              className="text-[11px] text-[#7A5440] mt-0.5 leading-relaxed"
              data-testid="addr-pin-status"
            >
              {form.coordinate ? (
                <span className="text-[#2D6A4F] flex items-start gap-1">
                  <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
                  <span>
                    Pin lokasi berhasil dipilih. Lokasi siap digunakan untuk
                    proses pengiriman.
                  </span>
                </span>
              ) : (
                <span>
                  Tambahkan pin lokasi agar kurir lebih mudah menemukan
                  alamat Anda. Pin lokasi juga diperlukan agar layanan kurir
                  instant seperti <b>GrabExpress</b> dan <b>GoSend</b>{" "}
                  tersedia.
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPinOpen(true)}
            className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded system-btn-primary transition shrink-0"
            data-testid="addr-pick-pin-btn"
          >
            {form.coordinate ? "Ubah Pin Lokasi" : "Pilih Pin Lokasi"}
          </button>
        </div>
      </div>

      <Button
        type="submit"
        disabled={saving}
        className="system-btn-primary rounded-xl"
        data-testid="addr-save-btn"
      >
        {saving ? "Menyimpan..." : "Simpan Alamat"}
      </Button>

      {pinOpen && (
        <Dialog open={pinOpen} onOpenChange={setPinOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Pilih Pin Lokasi Alamat</DialogTitle>
              <DialogDescription className="sr-only">
                Drag pin atau klik area peta untuk menentukan titik tepat.
              </DialogDescription>
            </DialogHeader>
            <LocationPicker
              value={form.coordinate || ""}
              onChange={(v) => upd("coordinate", v)}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPinOpen(false)}
                className="rounded-xl"
                data-testid="addr-pin-cancel"
              >
                Batal
              </Button>
              <Button
                type="button"
                onClick={() => setPinOpen(false)}
                className="system-btn-primary rounded-xl"
                data-testid="addr-pin-save"
              >
                Simpan Pin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </form>
  );
}
